import { useMemo } from 'react';
import {
  Projet,
  ProjetCalcule,
  DashboardData,
  DashboardKpis,
  DashboardAlerte,
  SanteFinanciere,
  ProjetASurveiller,
} from '../types';
import { calculerProjet } from './useProjetCalculations';
import { historiqueMensuel } from '../data/mockHistorique';

// ── Helpers ──

const fmt = (n: number) => Math.round(n).toLocaleString('fr-FR');

function calculerRetardSemaines(lot: { dateFinPrevue: string; statut: string }): number {
  if (lot.statut === 'termine') return 0;
  const finPrevue = new Date(lot.dateFinPrevue).getTime();
  const now = Date.now();
  if (now <= finPrevue) return 0;
  return Math.round((now - finPrevue) / (7 * 24 * 60 * 60 * 1000));
}

function scoreProjet(p: ProjetCalcule): number {
  let score = 100;

  // Pénalité budget : dépassement
  const depassement = p.budgetActuel - p.budgetSigne;
  if (depassement > 0) {
    const pctDepassement = (depassement / p.budgetSigne) * 100;
    score -= Math.min(pctDepassement * 4, 40);
  }

  // Pénalité retard lots
  const lotsEnRetard = p.lotsCalcules.filter(l => l.enRetard).length;
  score -= lotsEnRetard * 8;

  // Pénalité statut
  if (p.statut === 'a_risque') score -= 15;
  if (p.statut === 'en_pause') score -= 10;

  // Bonus terminé dans budget
  if (p.statut === 'termine' && depassement <= 0) score += 5;

  return Math.max(0, Math.min(100, Math.round(score)));
}

// ── Génération des alertes ──

function genererAlertes(projetsCalcules: ProjetCalcule[]): DashboardAlerte[] {
  const alertes: DashboardAlerte[] = [];
  let alerteId = 0;

  for (const p of projetsCalcules) {
    if (p.statut === 'termine') continue;

    const depassement = p.budgetActuel - p.budgetSigne;
    const pctDepassement = p.budgetSigne > 0 ? (depassement / p.budgetSigne) * 100 : 0;

    // 🔴 Dépassement budget > 5% non validé
    if (pctDepassement > 5) {
      alertes.push({
        id: `alerte-${++alerteId}`,
        niveau: 'rouge',
        projetId: p.id,
        projetNom: p.nom,
        probleme: `Dépassement budget de ${pctDepassement.toFixed(1)}%`,
        impact: `+${fmt(depassement)} €`,
        delaiAggravation: '< 1 semaine',
        source: 'automatique',
      });
    }

    // Analyser les retards par lot
    for (const lot of p.lotsCalcules) {
      const retardSemaines = calculerRetardSemaines(lot);

      // 🔴 Retard > 4 semaines
      if (retardSemaines > 4) {
        alertes.push({
          id: `alerte-${++alerteId}`,
          niveau: 'rouge',
          projetId: p.id,
          projetNom: p.nom,
          probleme: `${lot.nom} : retard de ${retardSemaines} semaines`,
          impact: `${retardSemaines} semaines`,
          delaiAggravation: 'Immédiat',
          source: 'automatique',
          lotId: lot.id,
        });
      }
      // 🟠 Retard 2-4 semaines
      else if (retardSemaines >= 2) {
        alertes.push({
          id: `alerte-${++alerteId}`,
          niveau: 'orange',
          projetId: p.id,
          projetNom: p.nom,
          probleme: `${lot.nom} : retard de ${retardSemaines} semaines`,
          impact: `${retardSemaines} semaines`,
          delaiAggravation: `< ${5 - retardSemaines} semaines`,
          source: 'automatique',
          lotId: lot.id,
        });
      }

      // ── Dérive précoce : productivité faible ──
      if (lot.statut === 'en_cours' && lot.productivite > 0 && lot.productivite < 50) {
        alertes.push({
          id: `alerte-${++alerteId}`,
          niveau: 'rouge',
          projetId: p.id,
          projetNom: p.nom,
          probleme: `${lot.nom} : productivité critique (${lot.productivite}%)`,
          impact: 'Risque retard majeur',
          delaiAggravation: '< 2 semaines',
          source: 'derive_tendance',
          lotId: lot.id,
        });
      } else if (lot.statut === 'en_cours' && lot.productivite > 0 && lot.productivite < 75) {
        alertes.push({
          id: `alerte-${++alerteId}`,
          niveau: 'orange',
          projetId: p.id,
          projetNom: p.nom,
          probleme: `${lot.nom} : productivité faible (${lot.productivite}%)`,
          impact: 'Risque retard',
          delaiAggravation: '< 4 semaines',
          source: 'derive_tendance',
          lotId: lot.id,
        });
      }

      // ── MAT en dégradation ──
      const avancementReel = lot.avancementPhysiqueReel ?? lot.pourcentageAvancement;
      if (avancementReel > 15 && lot.mat.tendance === 'degradation') {
        const niveauMAT = lot.mat.tauxMAT < 5 ? 'rouge' as const : 'orange' as const;
        alertes.push({
          id: `alerte-${++alerteId}`,
          niveau: niveauMAT,
          projetId: p.id,
          projetNom: p.nom,
          probleme: `${lot.nom} : MAT en dégradation (${lot.mat.tauxMAT.toFixed(1)}%)`,
          impact: `MAT ${fmt(lot.mat.margeATerminaison)} €`,
          delaiAggravation: '< 4 semaines',
          source: 'derive_tendance',
          lotId: lot.id,
        });
      }
    }

    // 🔴 Projet bloqué / en pause
    if (p.statut === 'en_pause') {
      alertes.push({
        id: `alerte-${++alerteId}`,
        niveau: 'rouge',
        projetId: p.id,
        projetNom: p.nom,
        probleme: 'Projet en pause / bloqué',
        impact: 'Calendrier impacté',
        delaiAggravation: 'En cours',
        source: 'automatique',
      });
    }

    // 🟠 Budget à 95% consommé, avancement < 90%
    if (p.pourcentageFinancier >= 95 && p.pourcentageFinancier < 100) {
      const avancementMoyen = p.lotsCalcules.length > 0
        ? Math.round(p.lotsCalcules.reduce((s, l) => s + l.pourcentageAvancement, 0) / p.lotsCalcules.length)
        : 0;
      if (avancementMoyen < 90) {
        alertes.push({
          id: `alerte-${++alerteId}`,
          niveau: 'orange',
          projetId: p.id,
          projetNom: p.nom,
          probleme: `Budget à ${p.pourcentageFinancier}% consommé, avancement ${avancementMoyen}%`,
          impact: `Risque dépassement`,
          delaiAggravation: '< 2 semaines',
          source: 'automatique',
        });
      }
    }

    // ── Alertes incidents ouverts ──
    for (const incident of p.incidentsOuverts) {
      const isSecurite = incident.type === 'securite';
      const isGrosImpact = (incident.impactFinancierEstime || 0) > 50000
        || (incident.impactDelaiEstime || 0) > 4;
      const niveau = (isSecurite || isGrosImpact) ? 'rouge' as const : 'orange' as const;
      alertes.push({
        id: `alerte-${++alerteId}`,
        niveau,
        projetId: p.id,
        projetNom: p.nom,
        probleme: `Incident : ${incident.titre}`,
        impact: incident.impactFinancierEstime
          ? `${fmt(incident.impactFinancierEstime)} €`
          : incident.impactDelaiEstime
            ? `${incident.impactDelaiEstime} sem.`
            : 'À évaluer',
        delaiAggravation: isSecurite ? 'Immédiat' : '< 2 semaines',
        source: 'incident',
        lotId: incident.lotId,
      });
    }
  }

  // Trier : rouge d'abord, puis orange
  alertes.sort((a, b) => {
    if (a.niveau === b.niveau) return 0;
    return a.niveau === 'rouge' ? -1 : 1;
  });

  return alertes.slice(0, 15);
}

// ── Hook principal ──

export function useDashboardCalculations(projets: Projet[]): DashboardData {
  return useMemo(() => {
    const projetsCalcules = projets.map(calculerProjet);
    const projetsActifs = projetsCalcules.filter(p => p.statut !== 'termine');

    // ── Alertes ──
    const alertes = genererAlertes(projetsCalcules);

    // ── KPIs Header ──
    // 1. Projection financière : positif = dépassement, négatif = économie
    const projectionGlobale = projetsCalcules.reduce((sum, p) => {
      return sum + (p.budgetActuel - p.budgetSigne);
    }, 0);

    // 2. Alertes critiques (rouges uniquement)
    const alertesCritiques = alertes.filter(a => a.niveau === 'rouge').length;
    const alertesTotal = alertes.length;

    // 3. Performance globale (score /100)
    const scores = projetsActifs.map(p => scoreProjet(p));
    const performanceGlobale = scores.length > 0
      ? Math.round(scores.reduce((s, v) => s + v, 0) / scores.length)
      : 100;

    // 4. Respect planning
    const projetsDansLesTemps = projetsActifs.filter(p =>
      p.lotsCalcules.every(l => !l.enRetard)
    ).length;
    const respectPlanning = projetsActifs.length > 0
      ? Math.round((projetsDansLesTemps / projetsActifs.length) * 100)
      : 100;

    // 5. MAT global
    const matGlobal = projetsCalcules.reduce((s, p) => s + p.mat.margeATerminaison, 0);
    const pvTerminaisonTotal = projetsCalcules.reduce((s, p) => s + p.mat.prixDeVenteTerminaison, 0);
    const tauxMATGlobal = pvTerminaisonTotal > 0
      ? (matGlobal / pvTerminaisonTotal) * 100
      : 0;

    // 6. Incidents ouverts
    const incidentsOuverts = projetsCalcules.reduce((s, p) => s + p.incidentsOuverts.length, 0);

    const kpis: DashboardKpis = {
      santeFinanciere: projectionGlobale,
      alertesCritiques,
      alertesTotal,
      performanceGlobale,
      respectPlanning,
      matGlobal,
      tauxMATGlobal,
      incidentsOuverts,
    };

    // ── Santé Financière ──
    const totalDerive = projetsCalcules.reduce((s, p) => {
      const d = p.budgetActuel - p.budgetSigne;
      return s + (d > 0 ? d : 0);
    }, 0);

    const prixDeVenteModifieTotalPortfolio = projetsCalcules.reduce(
      (s, p) => s + p.prixDeVenteModifieTotal, 0
    );
    const budgetActuelTotalPortfolio = projetsCalcules.reduce(
      (s, p) => s + p.budgetActuel, 0
    );
    const margeGlobalePortfolio = prixDeVenteModifieTotalPortfolio - budgetActuelTotalPortfolio;
    const tauxMargeGlobalPortfolio = prixDeVenteModifieTotalPortfolio > 0
      ? (margeGlobalePortfolio / prixDeVenteModifieTotalPortfolio) * 100
      : 0;

    // ── Projets à surveiller ──
    const projetsASurveiller: ProjetASurveiller[] = [];

    for (const p of projetsCalcules) {
      if (p.statut === 'termine') continue;

      const raisons: string[] = [];
      let urgence: 'haute' | 'moyenne' = 'moyenne';

      // MAT négatif
      if (p.mat.margeATerminaison < 0) {
        raisons.push(`MAT négatif (${fmt(p.mat.margeATerminaison)} €)`);
        urgence = 'haute';
      }

      // MAT en dégradation
      if (p.mat.tendance === 'degradation') {
        raisons.push('MAT en dégradation');
        if (p.mat.tauxMAT < 5) urgence = 'haute';
      }

      // Dépassement budget
      const derive = p.budgetActuel - p.budgetSigne;
      if (derive > 0) {
        const pct = p.budgetSigne > 0 ? (derive / p.budgetSigne) * 100 : 0;
        raisons.push(`Dépassement budget +${fmt(derive)} € (${pct.toFixed(1)}%)`);
        if (pct > 10) urgence = 'haute';
      }

      // Lots en retard
      const lotsRetard = p.lotsCalcules.filter(l => l.enRetard).length;
      if (lotsRetard > 0) {
        raisons.push(`${lotsRetard} lot${lotsRetard > 1 ? 's' : ''} en retard`);
      }

      // Statut à risque
      if (p.statut === 'a_risque') {
        raisons.push('Projet à risque');
        urgence = 'haute';
      }

      // Incidents ouverts
      if (p.incidentsOuverts.length > 0) {
        const nbInc = p.incidentsOuverts.length;
        raisons.push(`${nbInc} incident${nbInc > 1 ? 's' : ''} ouvert${nbInc > 1 ? 's' : ''}`);
        if (p.incidentsOuverts.some(i => i.type === 'securite')) urgence = 'haute';
      }

      if (raisons.length > 0) {
        projetsASurveiller.push({ projetId: p.id, projetNom: p.nom, raisons, urgence });
      }
    }

    // Trier : haute urgence d'abord, puis par nombre de raisons décroissant
    projetsASurveiller.sort((a, b) => {
      if (a.urgence !== b.urgence) return a.urgence === 'haute' ? -1 : 1;
      return b.raisons.length - a.raisons.length;
    });

    const santeFinanciere: SanteFinanciere = {
      projectionGlobale,
      totalDerive,
      margeGlobalePortfolio,
      tauxMargeGlobalPortfolio,
      matGlobal,
      tauxMATGlobal,
      projetsASurveiller,
      historiqueMensuel,
    };

    return {
      kpis,
      alertes,
      santeFinanciere,
      projetsCalcules,
    };
  }, [projets]);
}
